import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { fetchUserPostBySlug, toggleUserPostLike, toggleLike } from '../../services/api';
import Comments from './UserComments';
import OtherArticles from '../English/OtherArticles';

const UserArticlePage = () => {
    const { slug } = useParams();

    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSignInModalOpen, setIsSignInModalOpen] = useState(false);

    const [isLiked, setIsLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(0);

    const authToken = localStorage.getItem('authToken');
    const isUserLoggedIn = !!authToken;

    const viewCounted = useRef(false); // ✅ prevents double counting

    useEffect(() => {
        const getPost = async () => {
            try {
                const fetchedPost = await fetchUserPostBySlug(slug);
                setPost(fetchedPost);
                setLikeCount(fetchedPost.likes_count);
                setIsLiked(fetchedPost.is_liked);
            } catch (err) {
                setError('Error fetching the article.');
            } finally {
                setLoading(false);
            }
        };

        if (!viewCounted.current) {
            getPost();
            viewCounted.current = true;
        }
    }, [slug]);

    const handleLike = async () => {
    if (!isUserLoggedIn) {
        setIsSignInModalOpen(true);
        return;
    }

    try {
        // Use the correct toggle function depending on post type
        const response = await toggleUserPostLike(post.slug);

        if (response.liked !== undefined) {
            setIsLiked(response.liked);
            setLikeCount(response.likes_count);
        }
    } catch (err) {
        console.error('Error toggling like:', err);
    }
};


    const handleCloseModal = () => {
        setIsModalOpen(false);
        setIsSignInModalOpen(false);
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
    };

    if (loading) return <div className="text-center my-5">Loading...</div>;
    if (error) return <div className="text-center my-5 text-danger">{error}</div>;

    const imageUrl = post.featured_image
        ? post.featured_image.startsWith('http')
            ? post.featured_image
            : `https://res.cloudinary.com/dbm8xbouw/${post.featured_image}`
        : 'https://res.cloudinary.com/dnbbm9vzi/image/upload/v1736181865/web_development___website_webpage_browser_ad_advertisement_man_people_ibpgaq.png' ;

    return (
        <div className="container my-5">
            <div className="row justify-content-center">
                <div className="col-lg-8 col-md-10">

                    <h1 className="text-center mb-4 fw-bold">{post.title}</h1>

                    <p className="text-muted text-center mb-4">
                        <em>{post.excerpt}</em>
                    </p>

                    {imageUrl && (
                        <div className="text-center mb-4">
                            <img
                                src={imageUrl}
                                alt={post.title}
                                className="img-fluid rounded"
                                style={{ maxHeight: '400px', objectFit: 'cover' }}
                                onError={(e) => {
                                    e.currentTarget.src =
                                        'https://res.cloudinary.com/dnbbm9vzi/image/upload/v1736181865/web_development___website_webpage_browser_ad_advertisement_man_people_ibpgaq.png';
                                }}
                            />
                        </div>
                    )}

                    <div className="d-flex justify-content-center gap-3 mb-4">
                        <p><strong>Author:</strong> {post.author || 'Unknown'}</p>
                        {post.reviewed_by && (
                            <p>
                                <strong>Reviewed by:</strong> {post.reviewed_by}
                            </p>
                        )}

                        {post.category?.name && (
                            <p>
                                <strong>Category:</strong> {post.category.name} {/*({post.category.language}) */}
                            </p>
                        )}

                        <p><strong>Published on:</strong> {new Date(post.created_on).toLocaleDateString()}</p>
                    </div>

                    <div
                        className="mb-4"
                        dangerouslySetInnerHTML={{ __html: post.content }}
                    />

                    {/* Support Banner */}
                    <div
                        className="my-5 p-4 text-center"
                        style={{ backgroundColor: '#FFD700', borderRadius: '20px' }}
                    >
                        <h2 className="fw-bold mb-3">Like what you’re reading?</h2>
                        <p className="mb-4">
                            If this post earned a smile or a quiet “Okay, that was good,” consider dropping a small contribution.
                        </p>
                        <a
                            href="https://buymeacoffee.com/jessster"
                            className="btn btn-success btn-lg fw-bold"
                        >
                            Buy me a coffee
                        </a>
                    </div>

                    {/* Views / Share / Like */}
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <p className="mt-3">
                            <strong>Views:</strong> {post.number_of_views}
                        </p>

                        <div className="text-center">
                            <button
                                className="btn btn-dark fw-bold"
                                onClick={() => setIsModalOpen(true)}
                            >
                                Share
                            </button>
                        </div>

                        <button
                            className={`btn ${
                                isLiked ? 'btn-success' : 'btn-outline-success fw-bold'
                            }`}
                            onClick={handleLike}
                        >
                            {isLiked ? 'Unlike' : 'Like'} ({likeCount})
                        </button>
                    </div>

                    <hr />

                    {/* Other articles component */}
                    <OtherArticles currentSlug={post.slug}
                        currentLang={post.language}
                        limit={4} />

                    {/* Comments */}
                    <div className="container mt-5">
                        <h2 className="text-center fw-bold">Recent Chatter</h2>
                        <Comments
                            postSlug={slug}
                            isAuthenticated={isUserLoggedIn}
                            token={authToken}
                        />
                    </div>

                </div>
            </div>

            {/* Share Modal */}
            {isModalOpen && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content" style={{ backgroundColor: '#F9FAFC' }}>
                            <div className="modal-header">
                                <h5 className="modal-title fw-bold mx-auto">
                                    Share this article
                                </h5>
                                <button className="btn-close" onClick={handleCloseModal} />
                            </div>
                            <div className="modal-body">
                                <input
                                    type="text"
                                    className="form-control"
                                    value={window.location.href}
                                    readOnly
                                />
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={handleCloseModal}>
                                    Close
                                </button>
                                <button className="btn btn-success" onClick={handleCopyLink}>
                                    Copy Link
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Sign-in Modal */}
            {isSignInModalOpen && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content" style={{ backgroundColor: '#F9FAFC' }}>
                            <div className="modal-header">
                                <h5 className="modal-title fw-bold mx-auto">
                                    Sign In Required
                                </h5>
                                <button className="btn-close" onClick={handleCloseModal} />
                            </div>
                            <div className="modal-body text-center">
                                <p className="fw-bold">You need to sign in to like posts.</p>
                                <a href="/register" className="btn btn-dark btn-lg fw-bold">
                                    Sign Up
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserArticlePage;
